import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpdateCompanyDto, UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_COMPANIES } from "../GetCompanies";
import { ACTION_GET_ASSIGNED_COMPANIES } from "../GetAssignedCompanies/useGetAssignedCompanies";

import { companiesApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_COMPANY = "UPDATE_COMPANY";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface UpdateCompanyRequest {
  companyId: string;
  data: UpdateCompanyDto;
}

const updateCompany = async (params: UpdateCompanyRequest): Promise<void> => {
  const response = await companiesApi.companiesControllerUpdateCompany(
    params.companyId,
    params.data,
  );

  return response.data;
};

export const useUpdateCompany = (
  user: UserProfileDto | null,
): {
  updateCompany: (params: UpdateCompanyRequest) => void;
  updateCompanyEnabled: boolean;
  isSuccessUpdateCompany: boolean;
  isPendingUpdateCompany: boolean;
  isErrorUpdateCompany: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isPending, isError } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_UPDATE_COMPANY],
    mutationFn: enabled ? updateCompany : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_COMPANIES],
      });
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_ASSIGNED_COMPANIES],
      });
    },
  });

  return {
    updateCompany: mutate,
    updateCompanyEnabled: enabled,
    isSuccessUpdateCompany: isSuccess,
    isPendingUpdateCompany: isPending,
    isErrorUpdateCompany: isError,
  };
};
